import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

interface ServiceCardProps {
  title: string;
  description: string;
  imageSrc: string;
  index: number;
}

const ServiceCard: React.FC<ServiceCardProps> = ({
  title,
  description,
  imageSrc,
  index,
}) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ 
        duration: 0.5, 
        delay: index * 0.1,
        type: "spring",
        damping: 15,
        stiffness: 100
      }}
      whileHover={{ 
        y: -10,
        transition: { duration: 0.3 }
      }}
      className="group bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300"
    >
      <motion.div 
        initial={{ scale: 1 }}
        whileHover={{ scale: 1.05 }}
        transition={{ duration: 0.3 }}
        className="relative overflow-hidden h-64"
      >
        <motion.img
          initial={{ scale: 1.2 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.8 }}
          src={imageSrc}
          alt={title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="absolute inset-0 bg-dark/20 group-hover:bg-dark/30 transition-all duration-300"
        ></motion.div>
      </motion.div>
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.5 }}
        className="p-6"
      >
        <motion.h3 
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="text-2xl font-bold mb-3 text-dark"
        >
          {title}
        </motion.h3>
        <motion.p 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="text-gray-700"
        >
          {description}
        </motion.p>
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.5 }}
          className="mt-6 pt-6 border-t border-gray-200"
        >
          <motion.a
            whileHover={{ x: 10 }}
            whileTap={{ scale: 0.95 }}
            href="#contact"
            className="inline-flex items-center text-primary font-medium hover:text-dark transition-colors"
          >
            Learn More
            <motion.svg
              initial={{ x: 0 }}
              whileHover={{ x: 5 }}
              className="w-5 h-5 ml-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M14 5l7 7m0 0l-7 7m7-7H3"
              />
            </motion.svg>
          </motion.a>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

const Services = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const services = [
    {
      title: 'Custom Decks',
      description:
        'From intimate spaces to expansive entertainment areas, our deck designs integrate seamlessly with your home and landscape. We use premium materials like tropical hardwoods, composite, and sustainable cedar.',
      imageSrc: '/images/IMG_1818.jpeg',
    },
    {
      title: 'Pergolas & Arbors',
      description:
        'Add architectural interest and defined spaces to your outdoor living areas with our custom pergolas and arbors. These structures can provide shade, support climbing plants, and create beautiful transitional areas.',
      imageSrc: '/images/IMG_0258.jpeg',
    },
    {
      title: 'Patio Covers & Roof Extensions',
      description:
        'Extend your usable outdoor space with custom patio covers and roof extensions that provide protection from sun and rain while enhancing your home\'s aesthetic appeal.',
      imageSrc: '/images/Screenshot 2025-04-01 202737.png',
    },
    {
      title: 'Outdoor Kitchens',
      description:
        'Take outdoor entertaining to the next level with our custom outdoor kitchens. From simple grilling stations to comprehensive cooking and dining spaces, we create functional beauty.',
      imageSrc: '/service-kitchen.jpg',
    },
    {
      title: '3D Design & Visualization',
      description:
        'Have a look at your project before any ground work begins. Our design team creates beautiful 3D renderings tailored to your home and surrounding terrain, ensuring you get exactly what you want. Make confident decisions about your dream outdoor environment.',
      imageSrc: '/images/Website-set.png',
    },
  ];

  return (
    <section id="services" className="section bg-light">
      <div className="container">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h2 className="section-title">Our Premium Services</h2>
          <p className="section-subtitle mx-auto">
            We offer a comprehensive range of outdoor living solutions, each crafted with the highest standards of quality and attention to detail. From design to completion, we ensure your vision becomes reality.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-8">
          {services.map((service, index) => (
            <ServiceCard
              key={index}
              index={index}
              title={service.title}
              description={service.description}
              imageSrc={service.imageSrc}
            />
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-16 text-center"
        >
          <a href="#contact" className="btn btn-primary">
            Discuss Your Project
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Services; 